<template>
  <div id="sign-off">
    <div class="logo">
      <img src="/frontend/img/rr_logo.svg" />
    </div>
    <div class="formbox bg-white">
      <div id="content">
        <h1>{{ lang.headers.sign_off_vehicle }}</h1>
        <h3>{{ lang.headers.sub_header }}</h3>
        <p>{{ vehicle.name }} - {{ vehicle.license_plate }}</p>

        <div
          v-if="model.is_pick_up_costs_covered === false"
          class="flash warning"
        >
          <i class="fas fa-exclamation-triangle"></i>
          <h2>{{ lang.pick_up_costs.title }}</h2>
          {{ lang.pick_up_costs.body }}
        </div>

        <Form
          ref="form"
          :requiredFields="required_fields"
          :hiddenFields="hidden_fields"
          :disabledFields="disabled_fields"
          :onSubmit="onSubmit"
          :onSuccess="onSuccess"
          :onFail="onFail"
          :onDone="onDone"
        >
          <HiddenInput :model="model" name="id" />
          <HiddenInput :model="model" name="location_type_return" />

          <fieldset class="question1">
            <div class="item">
              <div class="question">
                <h3>{{ lang.headers.refueled }}</h3>
                <RadioGroup
                  :label="false"
                  :model="model"
                  :options="is_fully_refueled_options"
                  name="is_fully_refueled"
                  @change="onRefueledChange"
                />
              </div>
            </div>
          </fieldset>

          <fieldset
            class="question2"
            v-if="shouldAskChargingCable && currentStep > steps.VEHICLE_REFEULED"
          >
            <div class="item">
              <div class="question">
                <h3>{{ lang.headers.charging_cable }}</h3>
                <RadioGroup
                  :label="false"
                  :model="model"
                  :options="charging_cable_count_options"
                  name="charging_cable_count"
                  @change="onChargingCableChange"
                />
              </div>
            </div>
          </fieldset>

          <fieldset
            class="question3"
            v-if="currentStep > steps.VEHICLE_CHARGING_CABLE"
          >
            <div class="item">
              <div class="question">
                <h3>{{ lang.headers.cleanliness }}</h3>
                <RadioGroup
                  :label="false"
                  :model="model"
                  :options="is_fully_refueled_options"
                  name="is_vehicle_clean"
                  @change="onCleanlinessChange"
                />
              </div>
            </div>
          </fieldset>

          <fieldset
            class="question4"
            v-if="currentStep > steps.VEHICLE_CLEANLINESS"
          >
            <div class="item">
              <h3>{{ lang.headers.mileage }}</h3>
              <div class="formfield-full">
                <TextInput
                  :label="lang.mileage"
                  :model="model"
                  :multiline="false"
                  name="mileage"
                  @keypress="isNumber($event)"
                  :placeholder="mileageExample"
                />
              </div>
            </div>
          </fieldset>

          <fieldset
            class="question5"
            v-if="currentStep > steps.VEHICLE_MILEAGE"
          >
            <div class="item">
              <h3>{{ lang.headers.drop_off_location }}</h3>
              <FormGroup name="address_return">
                <FormGroupAddress
                  :withName="false"
                  :withRemark="false"
                  :model="model.address_return"
                  :countries="countries"
                  v-on:addressupdate="onUpdateMap"
                  @countryChanged="focusOnCountry"
                  :lookupAddress="true"
                  ref="address"
                />
              </FormGroup>

              <map-component
                contentId="none"
                ref="map"
                :myGeometry="dropoffLocation"
              />
            </div>
          </fieldset>

          <fieldset
            class="question6"
            v-if="currentStep > steps.VEHICLE_DROPOFF_LOCATION"
          >
            <div class="item">
              <h3>{{ lang.headers.drop_off_location_2 }}</h3>
              <FormGroup name="address_return">
                <TextInput
                  :label="lang.company_name"
                  :model="model.address_return"
                  :multiline="false"
                  name="name"
                  @change="onCompanyChange"
                  :placeholder="lang.company_name"
                />
                <TextInput
                  :label="lang.remark_car_key"
                  :model="model"
                  :multiline="false"
                  name="return_address_additional_remark"
                  :placeholder="lang.remark_car_key"
                />
              </FormGroup>
            </div>
          </fieldset>

          <div
            class="item question7"
            v-if="currentStep > steps.VEHICLE_DROPOFF_NAME"
          >
            <div class="question">
              <h3>{{ lang.headers.vehicle_images }}</h3>
              <p>{{ lang.headers.vehicle_images_subheader }}</p>
              <div class="row">
                <div>
                  <FormGroup name="files">
                    <single-file-upload
                      :model="model"
                      :indent="false"
                      v-on:upload-ready="onFileReady"
                      name="front"
                    />
                  </FormGroup>
                </div>

                <div>
                  <FormGroup name="files">
                    <single-file-upload
                      :model="model"
                      :indent="false"
                      v-on:upload-ready="onFileReady"
                      name="driver"
                    />
                  </FormGroup>
                </div>

                <div>
                  <FormGroup name="files">
                    <single-file-upload
                      :model="model"
                      :indent="false"
                      v-on:upload-ready="onFileReady"
                      name="back"
                    />
                  </FormGroup>
                </div>

                <div>
                  <FormGroup name="files">
                    <single-file-upload
                      :model="model"
                      :indent="false"
                      v-on:upload-ready="onFileReady"
                      name="passenger"
                    />
                  </FormGroup>
                </div>

                <div>
                  <FormGroup name="files">
                    <single-file-upload
                      :model="model"
                      :indent="false"
                      v-on:upload-ready="onFileReady"
                      name="dashboard"
                    />
                  </FormGroup>
                </div>
              </div>
            </div>
          </div>

          <fieldset
            class="question8"
            v-if="currentStep > steps.VEHICLE_CONDITION_IMAGES"
          >
            <div class="item">
              <div class="question">
                <h3>{{ lang.headers.has_damages }}</h3>
                <div>
                  <RadioGroup
                    :label="false"
                    :model="model"
                    :options="is_fully_refueled_options"
                    name="has_damages"
                    @change="onHasDamagesChange"
                  />
                </div>

                <div v-show="model.has_damages == 1">
                  <h3>{{ lang.headers.known_damages }}</h3>
                  <div class="row">
                    <Damages :damages="vehicle.damages" />
                    <div>
                      <FormGroup name="files">
                        <single-file-upload
                          :model="model"
                          :indent="false"
                          v-on:upload-ready="onFileReadyDamages"
                          name="damage"
                        />
                      </FormGroup>
                    </div>

                    <div>
                      <FormGroup name="files">
                        <single-file-upload
                          :model="model"
                          :indent="false"
                          v-on:upload-ready="onFileReadyDamages"
                          name="damage_overview"
                        />
                      </FormGroup>
                    </div>

                    <div>
                      <FormGroup name="files">
                        <single-file-upload
                          :model="model"
                          :indent="false"
                          v-on:upload-ready="onFileReadyDamages"
                          name="damage_additional"
                        />
                      </FormGroup>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </fieldset>
          <div class="question question9" v-if="enableSubmit">
            <h2>{{ lang.headers.questions }}</h2>
            <p>{{ lang.headers.questions_sub_header }}</p>
            <button
              :disabled="state.is_submitting || enableSubmit === false"
              @click="onSubmitClick"
              class="btn btn-secondary w-50"
            >
              {{ lang.submit_btn }}
            </button>
          </div>
        </Form>
      </div>
    </div>
  </div>
</template>

<style scoped>
#none {
  height: 320px;
}

.damages-wrapper[data-v-0514a3e4] {
  width: 100%;
}

.flash {
  position: relative;
  padding: 20px 85px 20px 20px;
  margin: 20px 0;
  background: rgb(248, 232, 225);
  background: linear-gradient(
    180deg,
    rgb(248, 232, 225) 70%,
    rgba(248, 220, 215, 1) 100%
  );
  border: 1px solid #f8dad7;
  border-radius: 10px;
  color: #ff8800;
}

.flash i {
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  text-align: center;
  width: 75px;
  color: #fff;
  font-size: 2em;
  padding: 25px 0 0 0;
  margin-left: 20px;
  background: rgb(255, 136, 0);
  background: linear-gradient(
    180deg,
    rgba(255, 136, 0, 1) 0%,
    rgba(255, 113, 0, 1) 100%
  );
  border-top-right-radius: 10px;
  border-bottom-right-radius: 10px;
}

.flash h2 {
  color: #ff8800;
}

.item {
  /* background: #ffffff; */
  padding: 20px;
  border-radius: 10px;
  position: relative;
  margin: 20px 0;
}
.question {
  display: inline-block;
}

.question-answer {
  display: inline-block;
  width: 100%;
}
.question span {
  margin-left: 20px;
}

.row {
  display: flex;
  flex-wrap: wrap;
  column-gap: 25px;
  margin-left: inherit !important;
}

.logo {
  padding: 0 20px;
}

.logo img {
  height: 77px;
}

.formbox {
  padding: 20px;
  margin: 20px;
  /* background: #ededed; */
}
</style>

<script>
import * as get from "lodash/get";
import ContractCustomerSignOff from "@/models/ContractCustomerSignOff";
import Contract, { LocationType, CreditType } from "@/models/Contract";
import Vehicle from "@/models/Vehicle";
import VehicleImage from "@/components/crud/vehicle/partials/VehicleImage.vue";
import {
  Form,
  FormGroupAddress,
  HiddenInput,
  TextInput,
  TextInputLicensePlate,
  FormGroup,
  DatePicker,
  TextReadonly,
  DateDifferenceInput,
  Select,
  RadioGroup,
  SingleFileUpload
} from "@/components/common/form";

import { VehicleItem } from "@/components/crud/vehicle";
import Damages from "./partials/Damages";
import FilterGroupCollection from "@/objects/FilterGroupCollection";
import ContractCustomerSignOffService from "@/services/ContractCustomerSignOffService";
import { mapGetters } from "vuex";
import { MapState } from "@/components/map/MapComponent";
import MapComponent from "../../map/MapComponent.vue";
import GoogleService from "@/services/GoogleService";
import dialogMixin from "@/mixins/dialog.mixin";
import { CookieService } from "@/services";
import dayjs from "dayjs";

let contractCustomerSignOffService;
const googleService = new GoogleService();

export default {
  name: "contract-signoff-edit",
  mixins: [dialogMixin],
  components: {
    TextInput,
    HiddenInput,
    TextInputLicensePlate,
    Select,
    RadioGroup,
    FormGroupAddress,
    DatePicker,
    DateDifferenceInput,
    VehicleItem,
    Form,
    TextReadonly,
    FormGroup,
    MapComponent,
    VehicleImage,
    SingleFileUpload,
    Damages
  },
  data() {
    return {
      state: {
        is_submitting: false,
        initialized: false
      },
      cookieService: new CookieService(),
      steps: {
        VEHICLE_REFEULED: 1,
        VEHICLE_CHARGING_CABLE: 2,
        VEHICLE_CLEANLINESS: 3,
        VEHICLE_MILEAGE: 4,
        VEHICLE_DROPOFF_LOCATION: 5,
        VEHICLE_DROPOFF_NAME: 6,
        VEHICLE_CONDITION_IMAGES: 7,
        VEHICLE_DAMAGED: 8,
        FORM_READY_TO_SUBMIT: 9
      },
      currentStep: 1,
      enableSubmit: false,
      showMap: false,
      required_fields: this.$route.params.required_fields,
      hidden_fields: this.$route.params.hidden_fields,
      disabled_fields: this.$route.params.disabled_fields || [],
      LocationType: LocationType,
      contract: this.$route.params.contract
        ? new Contract(this.$route.params.contract)
        : null,
      model: this.$route.params.model
        ? new ContractCustomerSignOff(this.$route.params.model)
        : null,
      countries: this.$route.params.countries,
      languages: this.$route.params.languages,
      vehicle: new Vehicle(this.$route.params.contract.vehicle),
      lang: i18n.app.components.customer_sign_off,
      mileageExample: "",
      dropoffLocation: {
        location: {
          lat: null,
          lng: null
        }
      },
      is_pick_up_costs_covered: this.$route.params.is_pick_up_costs_covered,
      is_fully_refueled_options: this.$route.params.is_fully_refueled_options,
      charging_cable_count_options: this.$route.params.charging_cable_count_options
    };
  },
  computed: {
    shouldAskChargingCable() {
      return Boolean(this.vehicle.should_ask_for_charging_cable_presence);
    }
  },
  mounted() {
    if (this.contract.id) {
      this.mileageExample = `Bijvb.: ${this.vehicle.mileage_current}`;
    }
    contractCustomerSignOffService = new ContractCustomerSignOffService(
      this.model.hash
    );
    const defaultCountryIds = this.getDefaultCountryIds();
  },
  watch: {
    'model.address_return': {
      deep: true,
      handler() {
        this.maybeAdvancePastDropOffLocationStep();
      }
    }
  },
  methods: {
    get: get,
    isNumber(evt) {
      evt = evt ? evt : window.event;
      const charCode = evt.which ? evt.which : evt.keyCode;
      const keyDigit0 = 48;
      const keyDigit9 = 57;
      const keySpace = 32;
      const keyDelete = 46;

      if (
        charCode >= keySpace &&
        (charCode < keyDigit0 || charCode > keyDigit9) &&
        charCode !== keyDelete
      ) {
        evt.preventDefault();
      } else {
        setTimeout(() => {
          this.onMileageChange();
        }, 200);

        return true;
      }
    },
    onCleanlinessChange() {
      if (this.currentStep === this.steps.VEHICLE_CLEANLINESS) {
        this.currentStep = this.steps.VEHICLE_MILEAGE;
        this.scrollTo();
      }
    },
    onMileageChange() {
      if (Number(this.model.mileage) >= Number(this.vehicle.mileage_current)) {
        if (this.currentStep === this.steps.VEHICLE_MILEAGE) {
          this.currentStep = this.steps.VEHICLE_DROPOFF_LOCATION;
          this.scrollTo();

          setTimeout(() => this.focusOnCountry, 1000);
        }
      }
    },
    focusOnCountry() {
      const country = this.$refs.address.getSelectedCountry();
      googleService.findByCity(country).then(coordsMap => {
        this.$refs.map.panTo(coordsMap.location);
      });
    },
    onUpdateMap(address) {
      const latRaw = address && address.latitude != null ? address.latitude : null;
      const lngRaw = address && address.longitude != null ? address.longitude : null;

      if (latRaw !== null && lngRaw !== null) {
        const coordsMap = { lat: Number(latRaw), lng: Number(lngRaw) };
        this.dropoffLocation = { location: coordsMap };
        this.showMap = true;
        this.model.address_return.latitude = latRaw;
        this.model.address_return.longitude = lngRaw;
        setTimeout(() => {
          if (this.$refs.map) {
            this.$refs.map.panTo(coordsMap, 18);
            this.$refs.map.$forceUpdate();
          }
        }, 300);
      } else {
        this.dropoffLocation = { location: { lat: null, lng: null } };
        this.showMap = false;
      }

      this.maybeAdvancePastDropOffLocationStep();
    },
    maybeAdvancePastDropOffLocationStep() {
      const addr = this.model.address_return || {};
      if (
        this.currentStep === this.steps.VEHICLE_DROPOFF_LOCATION &&
        addr.country_id &&
        addr.zip_code &&
        addr.house_number &&
        addr.street &&
        addr.city
      ) {
        this.currentStep = this.steps.VEHICLE_DROPOFF_NAME;
        this.scrollTo();
      }
    },
    onCompanyChange() {
      if (this.currentStep === this.steps.VEHICLE_DROPOFF_NAME) {
        this.currentStep = this.steps.VEHICLE_CONDITION_IMAGES;
        this.scrollTo();
      }
    },
    getLanguageIdsForCountryId(countryId) {
      if (this.languages) {
        let countryIds = this.languages
          .filter(language => {
            return language.data.country_ids.includes(countryId);
          })
          .map(language => language.value);

        return countryIds.length ? countryIds : [countryId];
      }
    },
    onFileReady() {
      const requiredFiles = [
        "front",
        "driver",
        "back",
        "passenger",
        "dashboard"
      ];

      if (this.checkFiles(requiredFiles) === 5) {
        if (this.currentStep === this.steps.VEHICLE_CONDITION_IMAGES) {
          this.currentStep = this.steps.VEHICLE_DAMAGED;
          this.scrollTo();
        }
      }
    },
    onFileReadyDamages() {
      const requiredFiles = ["damage", "damage_overview", "damage_additional"];

      if (this.checkFiles(requiredFiles) > 1) {
        this.enableSubmit = true;
        this.currentStep = this.steps.FORM_READY_TO_SUBMIT;
        this.scrollTo();
      }
    },
    checkFiles(requiredFiles) {
      let totalReady = 0;
      for (const [key, value] of Object.entries(this.model.files)) {
        if (value && requiredFiles.includes(key)) {
          totalReady++;
        }
      }

      return totalReady;
    },
    onRefueledChange() {
      if (this.currentStep === this.steps.VEHICLE_REFEULED) {
        this.currentStep = this.shouldAskChargingCable
          ? this.steps.VEHICLE_CHARGING_CABLE
          : this.steps.VEHICLE_CLEANLINESS;
        this.scrollTo();
      }
    },
    onChargingCableChange() {
      if (this.currentStep === this.steps.VEHICLE_CHARGING_CABLE) {
        this.currentStep = this.steps.VEHICLE_CLEANLINESS;
        this.scrollTo();
      }
    },
    onHasDamagesChange(newValue) {
      this.model.has_damages = newValue;

      this.enableSubmit = this.model.has_damages == 0;
      if (this.enableSubmit) {
        this.currentStep = this.steps.FORM_READY_TO_SUBMIT;
        this.scrollTo();
      }
    },
    getDefaultCountryIds() {
      let countryIds = this.getLanguageIdsForCountryId(
        this.model.address_return.country_id
      );

      return [...new Set(countryIds)];
    },
    onLanguageSelect(value) {
      if (!this.state.manually_selected_language_ids.includes(value)) {
        this.state.manually_selected_language_ids.push(value);
      }
    },
    onLanguageDeselect(value) {
      const index = this.state.manually_selected_language_ids.indexOf(value);

      if (index > -1 && this.manually_selected_language_ids) {
        this.manually_selected_language_ids.splice(index, 1);
      }
    },
    onSubmitClick() {
      this.state.is_submitting = true;
      delete this.model.address_return.additional_remark;
      this.$refs.form.submit(this.model);
    },
    onSuccess() {
      MapState.clear();
      FilterGroupCollection.clear();
      setTimeout(() => this.$router.push({ name: "sign-off-success" }));
    },
    onFail() {
      this.$forceUpdate();
    },
    onDone() {
      this.state.is_submitting = false;
    },
    onSubmit(model) {
      const method = this.model.id ? "update" : "insert";
      model.address_return.name = this.model.address_return.name;
      model.address_return.latitude = this.model.address_return.latitude;
      model.address_return.longitude = this.model.address_return.longitude;
      model.signed_off_at = dayjs().format("YYYY-MM-DD HH:mm");

      return contractCustomerSignOffService[method](model);
    },
    onCancelPress() {
      this.$router.push(`/sign-off/${$this.model.hash}/success`);
    },
    scrollTo() {
      const element = `.question${this.currentStep}`;
      setTimeout(() => {
        this.scrollToElement(jQuery(element));
      }, 300);
    },
    scrollToElement($element) {
      if ($element.length) {
        $("html, body").animate(
          {
            scrollTop: $element.offset().top
          },
          1000
        );
        return false;
      }
    },
    ...mapGetters({
      _client: "client",
      _clients: "clients"
    })
  }
};
</script>
